import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import SimpleLabelHeader from "./SimpleLabelHeader";
import SimpleLabelFilters from "./SimpleLabelFilters";
import SimpleLabelTable from "./SimpleLabelTable";
import SimpleLabelModal from "./SimpleLabelModal";
import CancelLabelModal from "./CancelLabelModal";
import RegenerateLabelModal from "./RegenerateLabelModal";
import CustomConfirmationDialog from "../../components/dialogs/CustomConfirmationDialog";
import { downloadFile } from "../../utils/files";
import LabelsService from "../../api/labels";

const mapBackendToFrontend = (backendLabel) => {
  return {
    id: backendLabel.id,
    productName: backendLabel.product_name,
    batchNumber: backendLabel.batch_number,
    manufactureDate: backendLabel.manufacture_date,
    expirationDate: backendLabel.expiration_date,
    netWeight: backendLabel.net_weight,
    ingredients: backendLabel.ingredients,
    barcode: backendLabel.barcode || "",
    prefab: backendLabel.prefab,
    status: backendLabel.status,
    previousBatch: backendLabel.previous_batch,
    replacedBy: backendLabel.replaced_by,
    cancelReason: backendLabel.cancel_reason,
    cancelledAt: backendLabel.cancelled_at,
    createdAt: backendLabel.created_at,
    deliveryCenter: backendLabel.delivery_center,
    deliveryCenterName: backendLabel.delivery_center_name,
  };
};

const mapFrontendToBackend = (frontendLabel) => {
  return {
    product_name: frontendLabel.productName,
    batch_number: frontendLabel.batchNumber,
    manufacture_date: frontendLabel.manufactureDate,
    expiration_date: frontendLabel.expirationDate,
    net_weight: frontendLabel.netWeight,
    ingredients: frontendLabel.ingredients,
    barcode: frontendLabel.barcode || "",
    prefab: frontendLabel.prefab,
    delivery_center: frontendLabel.deliveryCenter,
  };
};

const SimpleLabelPage = ({ showAlert, deliveryCenterId }) => {
  const [labels, setLabels] = useState([]);
  const [filteredLabels, setFilteredLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [labelToCancel, setLabelToCancel] = useState(null);

  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [labelToRegenerate, setLabelToRegenerate] = useState(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState(null);

  const [filters, setFilters] = useState({
    productName: "",
    startDate: null,
    endDate: null,
  });
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    fetchLabels();
  }, [deliveryCenterId]);

  useEffect(() => {
    applyFilters();
  }, [filters, labels]);

  const fetchLabels = async () => {
    setLoading(true);
    try {
      const params = {};

      const hasDeliveryCenterId =
        deliveryCenterId !== null && deliveryCenterId !== undefined;
      if (hasDeliveryCenterId) {
        params.delivery_center = deliveryCenterId;
      }

      const response = await LabelsService.getLabels(params);
      const backendLabels = response.data.results || response.data;
      const frontendLabels = backendLabels.map(mapBackendToFrontend);

      setLabels(frontendLabels);
    } catch (error) {
      console.error("Erro ao buscar etiquetas:", error);
      showAlert(
        "Falha ao carregar etiquetas. Por favor, tente novamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...labels];

    const hasProductNameFilter = filters.productName.trim() !== "";
    if (hasProductNameFilter) {
      const searchTerm = filters.productName.toLowerCase();
      filtered = filtered.filter((label) =>
        label.productName.toLowerCase().includes(searchTerm)
      );
    }

    const hasStartDate = filters.startDate !== null;
    if (hasStartDate) {
      const startDate = dayjs(filters.startDate).startOf("day");
      filtered = filtered.filter(
        (label) =>
          dayjs(label.manufactureDate).isAfter(startDate) ||
          dayjs(label.manufactureDate).isSame(startDate, "day")
      );
    }

    const hasEndDate = filters.endDate !== null;
    if (hasEndDate) {
      const endDate = dayjs(filters.endDate).endOf("day");
      filtered = filtered.filter(
        (label) =>
          dayjs(label.manufactureDate).isBefore(endDate) ||
          dayjs(label.manufactureDate).isSame(endDate, "day")
      );
    }

    setFilteredLabels(filtered);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      productName: "",
      startDate: null,
      endDate: null,
    });
  };

  const toggleFilters = () => {
    setFilterOpen((prev) => !prev);
  };

  const openModal = (label = null) => {
    const isEditMode = label !== null;
    setEditMode(isEditMode);
    setCurrentLabel(label);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentLabel(null);
    setEditMode(false);
  };

  const handleModalSuccess = async (message, labelData) => {
    try {
      if (editMode) {
        const backendData = mapFrontendToBackend(labelData);
        const response = await LabelsService.updateLabel(
          currentLabel.id,
          backendData
        );

        showAlert(message, "success");
        fetchLabels();
      } else {
        const backendData = {
          ...mapFrontendToBackend(labelData),
          delivery_center: deliveryCenterId,
        };

        const response = await LabelsService.createLabel(backendData);

        showAlert(message, "success");
        fetchLabels();
      }

      closeModal();
    } catch (error) {
      console.error("Erro ao salvar etiqueta:", error);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Falha ao salvar etiqueta. Por favor, tente novamente.";

      showAlert(errorMessage, "error");
    }
  };

  const openConfirmDialog = (labelId) => {
    setLabelToDelete(labelId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setLabelToDelete(null);
  };

  const confirmDeleteLabel = async () => {
    const hasLabelToDelete = labelToDelete !== null;
    if (!hasLabelToDelete) {
      return;
    }

    try {
      await LabelsService.deleteLabel(labelToDelete);
      showAlert("Etiqueta excluída com sucesso!", "success");
      fetchLabels();
    } catch (error) {
      console.error("Erro ao excluir etiqueta:", error);
      showAlert(
        "Falha ao excluir etiqueta. Por favor, tente novamente.",
        "error"
      );
    } finally {
      closeConfirmDialog();
    }
  };

  const handleDeleteLabel = (labelId) => {
    openConfirmDialog(labelId);
  };

  const openCancelModal = (label) => {
    setLabelToCancel(label);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setLabelToCancel(null);
  };

  const handleCancelLabelSuccess = async (message, cancelData) => {
    try {
      await LabelsService.cancelLabel(cancelData.labelId, cancelData.reason);
      showAlert(message, "success");
      fetchLabels();
      closeCancelModal();
    } catch (error) {
      console.error("Erro ao cancelar etiqueta:", error);

      const errorMessage =
        error.response?.data?.detail ||
        "Falha ao cancelar etiqueta. Por favor, tente novamente.";

      showAlert(errorMessage, "error");
    }
  };

  const openRegenerateModal = (label) => {
    setLabelToRegenerate(label);
    setRegenerateModalOpen(true);
  };

  const closeRegenerateModal = () => {
    setRegenerateModalOpen(false);
    setLabelToRegenerate(null);
  };

  const handleRegenerateLabelSuccess = async (message, regenerateData) => {
    try {
      const response = await LabelsService.regenerateLabel(
        labelToRegenerate.id,
        {
          batchNumber: regenerateData.batchNumber,
          manufactureDate: regenerateData.manufactureDate,
          expirationDate: regenerateData.expirationDate,
        }
      );

      showAlert(message, "success");
      fetchLabels();
      closeRegenerateModal();
    } catch (error) {
      console.error("Erro ao gerar novo lote:", error);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.batch_number?.[0] ||
        "Falha ao gerar novo lote. Por favor, tente novamente.";

      showAlert(errorMessage, "error");
    }
  };

  const exportToExcel = () => {
    const headers = [
      "Produto",
      "Modelo",
      "Lote",
      "Data Fabricação",
      "Data Validade",
      "Peso Líquido",
      "Ingredientes",
      "Código de Barras",
    ];

    const PREFAB_NAMES = {
      BASIC: "Básica",
      WITH_STORAGE: "Com Conservação",
      WITH_BARCODE: "Com Código Barras",
      WITH_QRCODE: "Com QR Code",
      COMPLETE: "Completa",
    };

    const rows = filteredLabels.map((label) => [
      label.productName,
      PREFAB_NAMES[label.prefab] || "Básica",
      label.batchNumber,
      dayjs(label.manufactureDate).format("DD/MM/YYYY"),
      dayjs(label.expirationDate).format("DD/MM/YYYY"),
      label.netWeight,
      label.ingredients,
      label.barcode || "N/A",
    ]);

    let csvContent = "\uFEFF";
    csvContent += headers.join(";") + "\n";

    rows.forEach((row) => {
      csvContent += row.join(";") + "\n";
    });

    const filename = `etiquetas-${dayjs().format("YYYY-MM-DD")}.csv`;
    downloadFile(csvContent, filename);

    showAlert("Arquivo exportado com sucesso!", "success");
  };

  const refreshData = () => {
    fetchLabels();
  };

  return (
    <Box sx={{ p: 6 }}>
      <CustomConfirmationDialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDeleteLabel}
        title="Excluir Etiqueta?"
        dialogContent="Tem certeza que deseja excluir esta etiqueta?"
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      <SimpleLabelHeader
        onAddLabel={() => openModal()}
        onExport={exportToExcel}
      />

      <Box sx={{ p: 2 }}>
        <SimpleLabelFilters
          filters={filters}
          filterOpen={filterOpen}
          handleFilterChange={handleFilterChange}
          resetFilters={resetFilters}
          toggleFilters={toggleFilters}
          applyFilters={applyFilters}
          refreshData={refreshData}
          exportExcel={exportToExcel}
        />

        <SimpleLabelTable
          labels={filteredLabels}
          onEditLabel={openModal}
          onDeleteLabel={handleDeleteLabel}
          onAddLabel={() => openModal()}
          onCancelLabel={openCancelModal}
          onRegenerateLabel={openRegenerateModal}
          loading={loading}
        />
      </Box>

      <SimpleLabelModal
        isOpen={modalOpen}
        onClose={closeModal}
        editMode={editMode}
        initialData={currentLabel}
        onSuccess={handleModalSuccess}
        deliveryCenterId={deliveryCenterId}
      />

      <CancelLabelModal
        isOpen={cancelModalOpen}
        onClose={closeCancelModal}
        label={labelToCancel}
        onSuccess={handleCancelLabelSuccess}
      />

      <RegenerateLabelModal
        isOpen={regenerateModalOpen}
        onClose={closeRegenerateModal}
        label={labelToRegenerate}
        onSuccess={handleRegenerateLabelSuccess}
      />
    </Box>
  );
};

export default SimpleLabelPage;
